import { RefObject } from 'react'
import { TFocusMode } from './types'
import { BLUR_MODE } from './consts'

/**
 * Applies height and overflow styles to the textarea based on focus state.
 * If `lockedHeight` is defined, it enforces a fixed height and hides overflow.
 * Otherwise, it auto-adjusts height and sets overflow depending on content and focus.
 */
export const setTextAreaHeight = (
  textAreaElement: HTMLTextAreaElement,
  options: {
    lockedHeight?: number
    maxHeight: number
    mode: TFocusMode
  }
) => {
  const { lockedHeight, maxHeight, mode } = options

  // Reset height to auto to allow accurate measurement of scrollHeight
  textAreaElement.style.height = 'auto'
  const scrollHeight = textAreaElement.scrollHeight

  if (lockedHeight) {
    textAreaElement.style.height = `${lockedHeight}px`
  } else {
    textAreaElement.style.height = `${scrollHeight + 2}px`
  }

  if (mode === BLUR_MODE) {
    textAreaElement.style.overflowY = 'hidden'
  } else {
    // when in focus, set overflowY based on scroll height
    textAreaElement.style.overflowY =
      scrollHeight >= maxHeight ? 'auto' : 'hidden'
  }
}

/**
 * Reconstructs textarea value after a paste event,
 * inserting the pasted content at the correct cursor location.
 */
export const getValueAfterTextAreaPaste = (
  textAreaElement: HTMLTextAreaElement,
  event: React.ClipboardEvent<HTMLTextAreaElement>
): string => {
  event.preventDefault()

  const { selectionStart = 0, selectionEnd = 0 } = textAreaElement
  const pastedText = event.clipboardData?.getData('text') ?? ''
  const value = textAreaElement.value

  const updatedValue =
    value.slice(0, selectionStart) + pastedText + value.slice(selectionEnd)

  // Restore cursor position after paste
  const newCursorPos = selectionStart + pastedText.length
  textAreaElement.setSelectionRange(newCursorPos, newCursorPos)

  return updatedValue
}

/**
 * Returns the new textarea value after a paste event,
 * taking into account a maxLength if provided.
 */
export const getTextAreaPastedValue = (
  event: React.ClipboardEvent<HTMLTextAreaElement>,
  textAreaRef: RefObject<HTMLTextAreaElement | null>,
  maxLength?: number
): string | undefined => {
  const textAreaElement = textAreaRef.current

  if (!textAreaElement) return

  const newValue = getValueAfterTextAreaPaste(textAreaElement, event)
  return maxLength ? newValue.slice(0, maxLength) : newValue
}

/**
 * Scrolls the textarea to the top and hides vertical overflow.
 */

export const scrollTextAreaToTopAndHideOverflow = (
  textAreaRef?: RefObject<HTMLTextAreaElement | null>
) => {
  if (textAreaRef?.current) {
    textAreaRef.current.scrollTo({ top: 0 })
    textAreaRef.current.style.overflowY = 'hidden'
  }
}
